// src/pages/Admin/Companies.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch, clearStoredCredentials } from '../../utils/api';

// PrimeReact Components
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FilterMatchMode, FilterOperator } from 'primereact/api';

// 🎨 Orange Theme Colors
const BRAND_ORANGE = '#F89423';
const BRAND_ORANGE_DARK = '#ea7c1f';
const BRAND_ORANGE_LIGHT = '#fef3e2';

const API_BASE = process.env.REACT_APP_API_URL || 'https://fslapi.devformula.com/api';

function Companies() {
  const navigate = useNavigate();
  const location = useLocation();
  const dt = useRef(null);
  const deleteRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  
  // ✅ Advanced Filter State
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    businessName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    contactName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    accountType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    isOnboarded: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);
  const [formData, setFormData] = useState({
    businessName: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    title: '',
    accountType: '',
    businessSector: '',
    businessCategory: '',
    startDate: '',
    endDate: '',
    isOnboarded: true,
    isSchedule: false,
    isCompleteRegistration: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  const [dropdownOptions, setDropdownOptions] = useState({
    titles: [],
    businessSectors: [],
    businessCategories: []
  });

  // ✅ Account Type Options
  const ACCOUNT_TYPE_OPTIONS = [
    { label: 'Corporate', value: 'Corporate' },
    { label: 'Individual', value: 'Individual' }
  ];

  // ✅ Status Options
  const STATUS_OPTIONS = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];

  // ==========================================
  // AUTH HEADERS HELPER
  // ==========================================
  const getAuthHeaders = () => {
    const session = JSON.parse(localStorage.getItem('fsl_admin_session') || '{}');
    return {
      'AuthorizationKey': session.authorizationKey || session.AuthorizationKey || '',
      'Token': session.token || session.Token || '',
      'SecretKey': session.secretKey || session.SecretKey || ''
    };
  };

  // ==========================================
  // SECURE DOCUMENT FETCHER
  // ==========================================
  const fetchDocumentWithAuth = async (fileName, documentType) => {
    if (!fileName) return null;
    try {
      const url = `${API_BASE}/settings/company-document/${documentType}/${encodeURIComponent(fileName)}`;
      const headers = getAuthHeaders();
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) throw new Error(`Failed to fetch document: ${response.status}`);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching document:', error);
      return null;
    }
  };

  const revokeBlobUrl = (url) => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (viewingCompany?.logo) {
      fetchDocumentWithAuth(viewingCompany.logo, 'logo').then(url => setLogoBlobUrl(url));
    } else {
      setLogoBlobUrl(null);
    }
    return () => revokeBlobUrl(logoBlobUrl);
  }, [viewingCompany]);

  // ==========================================
  // AUTH CHECK & DATA FETCHING
  // ==========================================
  useEffect(() => {
    const adminSession = localStorage.getItem('fsl_admin_session');
    if (!adminSession) {
      toast.error('Please log in as admin');
      navigate('/admin-login', { replace: true });
      return;
    }
    fetchCompanies();
  }, [navigate]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`${API_BASE}/hmo/list-onboarded-companies`, 'POST', {});
      if (response?.status === 'success') {
        const formattedCompanies = (response.data || []).map(company => ({
          companyId: company.companyId,
          businessName: company.businessName || 'N/A',
          contactName: `${company.firstName || ''} ${company.lastName || ''}`.trim() || 'N/A',
          email: company.email || 'N/A',
          phoneNumber: company.phoneNumber || 'N/A',
          accountType: company.accountType || 'N/A',
          isSchedule: company.isSchedule ?? false,
          isCompleteRegistration: company.isCompleteRegistration ?? false,
          ninDocument: company.ninDocument || company.NinDocument || null,
          cacDocument: company.cacDocument || company.CacDocument || null,
          logo: company.logo || company.Logo || null,
          startDate: company.startDate ? new Date(company.startDate).toLocaleDateString() : 'N/A',
          endDate: company.endDate ? new Date(company.endDate).toLocaleDateString() : 'N/A',
          isOnboarded: company.isOnboarded,
          createdDate: company.createdDate ? new Date(company.createdDate).toLocaleDateString() : 'N/A',
          _full: company
        }));
        setCompanies(formattedCompanies);
        if (response.dropdowns) {
          setDropdownOptions({
            titles: response.dropdowns.titles || [],
            businessSectors: response.dropdowns.businessSectors || [],
            businessCategories: response.dropdowns.businessCategories || []
          });
        }
      }
    } catch (error) {
      console.error('💥 Fetch companies error:', error);
      toast.error('Could not load companies');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // PDF/DOCUMENT HELPERS
  // ==========================================
  const convertBase64ToBlob = (base64Data, contentType = 'application/pdf') => {
    const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');
    const binary = atob(base64);
    const buffer = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i);
    return new Blob([buffer], { type: contentType });
  };

  const downloadDocument = async (fileName, documentType, title = 'Document') => {
    if (!fileName) {
      toast.error('No document available to download');
      return;
    }
    try {
      let blob = null;
      let finalFileName = fileName.includes('.') ? fileName : `${title}.pdf`;
      if (fileName.startsWith('data:application/pdf;base64,') || (/^[A-Za-z0-9+/=]+$/.test(fileName) && fileName.length > 100)) {
        const base64 = fileName.startsWith('data:') ? fileName : `data:application/pdf;base64,${fileName}`;
        blob = convertBase64ToBlob(base64);
      } else {
        const fileUrl = `${API_BASE}/settings/company-document/${documentType}/${encodeURIComponent(fileName)}`;
        const headers = getAuthHeaders();
        const response = await fetch(fileUrl, { method: 'GET', headers });
        if (!response.ok) throw new Error(`Failed to fetch document: ${response.status}`);
        blob = await response.blob();
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${title} download started`);
    } catch (error) {
      console.error('❌ Document download error:', error);
      toast.error(error.message || `Failed to download ${title}`);
    }
  };

  const viewDocument = async (fileName, documentType, title = 'Document') => {
    if (!fileName) {
      toast.error('No document available to view');
      return;
    }
    try {
      let url = null;
      if (fileName.startsWith('data:application/pdf;base64,') || (/^[A-Za-z0-9+/=]+$/.test(fileName) && fileName.length > 100)) {
        const base64 = fileName.startsWith('data:') ? fileName : `data:application/pdf;base64,${fileName}`;
        const blob = convertBase64ToBlob(base64);
        url = URL.createObjectURL(blob);
      } else {
        const fileUrl = `${API_BASE}/settings/company-document/${documentType}/${encodeURIComponent(fileName)}`;
        const headers = getAuthHeaders();
        const response = await fetch(fileUrl, { method: 'GET', headers });
        if (!response.ok) throw new Error(`Failed to fetch document: ${response.status}`);
        const blob = await response.blob();
        url = URL.createObjectURL(blob);
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      console.error('❌ Document view error:', error);
      toast.error(error.message || `Failed to open ${title}`);
    }
  };

  // ==========================================
  // MODAL HANDLERS
  // ==========================================
  const handleViewDetails = (company) => {
    setViewingCompany(company);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingCompany(null);
    revokeBlobUrl(logoBlobUrl);
    setLogoBlobUrl(null);
  };

  const handleOpenModal = (company = null) => {
    if (company?._full) {
      setEditingCompany(company);
      const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      };
      setFormData({
        businessName: company._full.businessName || '',
        firstName: company._full.firstName || '',
        lastName: company._full.lastName || '',
        email: company._full.email || '',
        phoneNumber: company._full.phoneNumber || '',
        title: company._full.title || '',
        accountType: company._full.accountType || '',
        businessSector: company._full.businessSector || '',
        businessCategory: company._full.businessCategory || '',
        startDate: formatDateForInput(company._full.startDate),
        endDate: formatDateForInput(company._full.endDate),
        isOnboarded: company._full.isOnboarded ?? true,
        isSchedule: company._full.isSchedule ?? false,
        isCompleteRegistration: company._full.isCompleteRegistration ?? false
      });
    } else {
      setEditingCompany(null);
      const today = new Date().toISOString().split('T')[0];
      const nextYear = new Date();
      nextYear.setFullYear(nextYear.getFullYear() + 1);
      setFormData({
        businessName: '', firstName: '', lastName: '', email: '', phoneNumber: '',
        title: '', accountType: '', businessSector: '', businessCategory: '',
        startDate: today, endDate: nextYear.toISOString().split('T')[0],
        isOnboarded: true, isSchedule: false, isCompleteRegistration: false
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCompany(null);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.businessName?.trim()) errors.businessName = 'Business name is required';
    if (!formData.email?.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      errors.endDate = 'End date must be after start date';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        CompanyId: editingCompany?._full?.companyId,
        BusinessName: formData.businessName.trim(),
        FirstName: formData.firstName.trim(),
        LastName: formData.lastName.trim(),
        Email: formData.email.trim().toLowerCase(),
        PhoneNumber: formData.phoneNumber.trim(),
        Title: formData.title.trim(),
        AccountType: formData.accountType.trim(),
        BusinessSector: formData.businessSector.trim(),
        BusinessCategory: formData.businessCategory.trim(),
        StartDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        EndDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        IsOnboarded: formData.isOnboarded,
        IsSchedule: formData.isSchedule,
        IsCompleteRegistration: formData.isCompleteRegistration
      };
      let response;
      if (editingCompany) {
        response = await authenticatedFetch(`${API_BASE}/hmo/update-company`, 'PUT', payload);
        if (response?.status === 'success') {
          toast.success(response.message || 'Company updated successfully');
        } else {
          throw new Error(response?.message || 'Failed to update company');
        }
      } else {
        response = await authenticatedFetch(`${API_BASE}/hmo/create-company`, 'POST', payload);
        if (response?.status === 'success') {
          toast.success(response.message || 'Company added successfully');
        } else {
          throw new Error(response?.message || 'Failed to add company');
        }
      }
      handleCloseModal();
      fetchCompanies();
    } catch (error) {
      toast.error(error.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ==========================================
  // DELETE & TOGGLE STATUS
  // ==========================================
  const handleDelete = (event, company) => {
    confirmPopup({
      target: event.currentTarget,
      message: `Are you sure you want to remove "${company.businessName}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const response = await authenticatedFetch(`${API_BASE}/hmo/delete-company`, 'DELETE', { companyId: company.companyId });
          if (response?.status === 'success') {
            toast.success(response.message || 'Company removed successfully');
            fetchCompanies();
          } else {
            throw new Error(response?.message || 'Delete failed');
          }
        } catch (error) {
          toast.error(error.message || 'Failed to delete company');
        }
      },
      reject: () => toast.info('Delete cancelled')
    });
  };

  const handleToggleStatus = async (companyId, currentStatus, email) => {
    if (!email) {
      toast.error('Company email not available. Cannot toggle status.');
      return;
    }
    try {
      const response = await authenticatedFetch(`${API_BASE}/hmo/complete-onboarding`, 'POST', { Email: email });
      if (response?.status === 'success') {
        toast.success(response.message || `Company ${!currentStatus ? 'activated' : 'deactivated'}`);
        fetchCompanies();
      } else {
        throw new Error(response?.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleViewHmoStaff = (companyId) => {
    navigate(`/admin/companies/${companyId}/hmo-staff`);
  };

  // ==========================================
  // FILTER & NAVIGATION
  // ==========================================
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      businessName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      contactName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
      accountType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
      isOnboarded: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
    });
    setGlobalFilterValue('');
  };

  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('fsl_admin_session');
    toast.success('Logged out successfully');
    navigate('/admin-login', { replace: true });
  };

  const handleNavClick = (path, e) => {
    e?.preventDefault();
    if (path) navigate(path);
    if (window.innerWidth < 992) setSidebarOpen(false);
  };

  const getDropdownName = (dropdown, id) => {
    if (!id) return 'N/A';
    const item = dropdown.find(d => String(d.id) === String(id) || d.name === id);
    return item ? item.name : id;
  };

  const navItems = useMemo(() => {
    const currentPath = location.pathname;
    return [
      { section: 'MAIN', items: [{ name: 'Get Started', icon: 'fa-home', path: '/get-started', active: currentPath === '/get-started' }] },
      { section: 'DASHBOARD', items: [{ name: 'Analytics', icon: 'fa-chart-bar', path: '/hmoadmin', active: currentPath === '/hmoadmin' }] },
      {
        section: 'ADMIN',
        items: [
          { name: 'Client', icon: 'fa-building', path: '/admin/companies', active: currentPath.startsWith('/admin/companies') },
          { name: 'Send Document', icon: 'fa-file-contract', path: '/admin/company-documents', active: location.pathname.startsWith('/admin/company-documents') },
          { name: 'Settings', icon: 'fa-cog', path: '/admin/crud/title', active: currentPath.startsWith('/admin/crud') }
        ]
      }
    ];
  }, [location.pathname]);

  // ==========================================
  // TEMPLATES
  // ==========================================
  const actionBodyTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button icon="pi pi-eye" className="p-button-rounded p-button-info p-button-sm" onClick={() => handleViewDetails(rowData)} tooltip="View Details" style={{ backgroundColor: '#06b6d4', borderColor: '#06b6d4' }} />
      <Button icon="pi pi-users" className="p-button-rounded p-button-success p-button-sm" onClick={() => handleViewHmoStaff(rowData.companyId)} tooltip="View HMO Staff" style={{ backgroundColor: '#10b981', borderColor: '#10b981' }} />
      <Button icon="pi pi-pencil" className="p-button-rounded p-button-primary p-button-sm" onClick={() => handleOpenModal(rowData)} tooltip="Edit" style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }} />
      <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-sm" onClick={(e) => handleDelete(e, rowData)} tooltip="Delete" ref={deleteRef} />
    </div>
  );

  // ✅ CUSTOM TOGGLE SWITCH
  const statusBodyTemplate = (rowData) => {
    return (
      <label className="toggle-switch" title="Click to toggle status">
        <input 
          type="checkbox" 
          checked={rowData.isOnboarded} 
          onChange={() => handleToggleStatus(rowData.companyId, rowData.isOnboarded, rowData.email)}
          disabled={!rowData.email}
        />
        <span className="toggle-slider"></span>
      </label>
    );
  };

  const accountTypeBodyTemplate = (rowData) => (
    <Tag value={rowData.accountType || 'N/A'} severity={rowData.accountType === 'Corporate' ? 'primary' : 'secondary'} className="fw-semibold" />
  );

  const scheduleBodyTemplate = (rowData) => (
    <Tag value={rowData.isSchedule ? 'Scheduled' : 'Not Scheduled'} severity={rowData.isSchedule ? 'success' : 'warning'} className="fw-semibold" />
  );

  const registrationBodyTemplate = (rowData) => (
    <Tag value={rowData.isCompleteRegistration ? 'Complete' : 'Incomplete'} severity={rowData.isCompleteRegistration ? 'success' : 'secondary'} className="fw-semibold" />
  );

  const documentBadgeTemplate = (hasDocument) => (
    <Tag value={hasDocument ? 'Uploaded' : 'Missing'} severity={hasDocument ? 'success' : 'danger'} className="fw-semibold" />
  );

  const renderHeader = () => (
    <div className="flex justify-content-between align-items-center flex-wrap gap-3">
      <div className="flex align-items-center gap-2">
        <Button type="button" icon="pi pi-filter-slash" label="Clear Filters" outlined onClick={clearFilter} size="small" style={{ borderRight: '1px solid #e5e7eb', paddingRight: '1rem', marginRight: '0.5rem', marginTop: '1rem', borderRadius: '6px 0 0 6px' }} />
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" style={{ width: '250px', borderRadius: '0 6px 6px 0', borderLeft: 'none', marginTop: '1rem' }} />
        </IconField>
      </div>
    </div>
  );

  const header = renderHeader();

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div className="admin-dashboard-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      <ConfirmPopup />
      
      {/* SIDEBAR */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="brand-logo">
            <img src="../images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
          </Link>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((section, idx) => (
            <div key={idx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx}>
                    <Link to={item.path || '#'} className={`nav-link ${item.active ? 'active' : ''}`} onClick={(e) => item.path && handleNavClick(item.path, e)}>
                      <i className={`fas ${item.icon} me-2`}></i>{item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <button className="nav-link" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>Logout
              </button>
            </ul>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="page-title">
              <h4 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>Clients</h4>
            </div>
          </div>
          <div className="header-right">
            <button className="header-icon"><i className="fas fa-search"></i></button>
            <button className="header-icon"><i className="fas fa-bell"></i><span className="badge">2</span></button>
            <div className="user-avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', background: `linear-gradient(135deg, ${BRAND_ORANGE} 0%, ${BRAND_ORANGE_DARK} 100%)`, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', cursor: 'pointer' }}>AD</div>
          </div>
        </header>

        <div className="content-wrapper" style={{ padding: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h5 className="mb-1">Manage Clients</h5>
              <p className="text-muted small mb-0">{companies.length} Clients</p>
            </div>
          </div>

          {/* ✅ FIXED: Horizontal Scroll Container */}
          <div className="table-scroll-wrapper" style={{ overflowX: 'auto', width: '100%' }}>
            <Card className="border-0 shadow-sm p-5" data-aos="fade-up" data-aos-delay="100">
              <div className="card-body p-0">
                <DataTable 
                  ref={dt} 
                  value={companies} 
                  paginator 
                  showGridlines 
                  rows={10}
                  rowsPerPageOptions={[5, 10, 25, 50]} 
                  loading={loading} 
                  dataKey="companyId"
                  filters={filters} 
                  globalFilterFields={['businessName', 'contactName', 'email', 'accountType']}
                  header={header} 
                  emptyMessage="No clients found." 
                  onFilter={(e) => setFilters(e.filters)}
                  tableStyle={{ minWidth: '700px' }}
                  className="p-datatable-sm"
                >
                  <Column field="companyId" header="S/N" sortable style={{ width: '80px' }} body={(rowData, { rowIndex }) => rowIndex + 1} />
                  <Column field="businessName" header="Company Name" sortable filter filterPlaceholder="Search company" style={{ minWidth: '12rem' }} body={(rowData) => (
                    <div>
                      <div className="fw-semibold">{rowData.businessName}</div>
                      <small className="text-muted">Added: {rowData.createdDate}</small>
                    </div>
                  )} />
                  <Column field="contactName" header="Contact" sortable filter filterPlaceholder="Search contact" style={{ minWidth: '10rem' }} />
                  <Column field="isOnboarded" header="Status" sortable style={{ width: '100px' }} body={statusBodyTemplate} filter filterField="isOnboarded" showFilterMatchModes={false} filterMenuStyle={{ width: '14rem' }} filterElement={(options) => (
                    <Dropdown value={options.value} options={STATUS_OPTIONS} onChange={(e) => options.filterCallback(e.value, options.index)} optionLabel="label" optionValue="value" placeholder="Select Status" className="p-column-filter" showClear />
                  )} />
                  <Column header="Actions" body={actionBodyTemplate} style={{ width: '200px' }} exportable={false} frozen />
                </DataTable>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* VIEW DETAILS MODAL */}
      {showViewModal && viewingCompany && (
        <div className="modal-overlay" onClick={handleCloseViewModal} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', width: '95%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 10, borderRadius: '12px 12px 0 0' }}>
              <div className="d-flex align-items-center gap-3">
                {logoBlobUrl ? (
                  <img src={logoBlobUrl} alt="Logo" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                ) : (
                  <div style={{ width: '50px', height: '50px', backgroundColor: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-building text-muted"></i>
                  </div>
                )}
                <div>
                  <h5 style={{ margin: 0, color: '#1f2937', fontWeight: '700' }}>{viewingCompany.businessName}</h5>
                  <small className="text-muted">Company Details</small>
                </div>
              </div>
              <button onClick={handleCloseViewModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '24px' }}>
              <div className="d-flex flex-wrap gap-2 mb-4">
                <span className={`badge ${viewingCompany.isOnboarded ? 'bg-success' : 'bg-secondary'}`} style={{ padding: '6px 14px', borderRadius: '12px', fontSize: '12px' }}>{viewingCompany.isOnboarded ? '● Active' : '● Inactive'}</span>
                <span className={`badge ${viewingCompany.isSchedule ? 'bg-info' : 'bg-warning text-dark'}`} style={{ padding: '6px 14px', borderRadius: '12px', fontSize: '12px' }}>{viewingCompany.isSchedule ? '● Scheduled' : '● Not Scheduled'}</span>
                <span className={`badge ${viewingCompany.isCompleteRegistration ? 'bg-success' : 'bg-secondary'}`} style={{ padding: '6px 14px', borderRadius: '12px', fontSize: '12px' }}>{viewingCompany.isCompleteRegistration ? '● Registration Complete' : '● Registration Incomplete'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '20px', border: '1px solid #e5e7eb' }}>
                  <h6 style={{ color: '#F89423', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><i className="fas fa-building"></i> Business Information</h6>
                  <div className="mb-3"><small className="text-muted d-block" style={{ fontSize: '11px', fontWeight: '600' }}>ACCOUNT TYPE</small><p className="mb-0" style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>{getDropdownName(dropdownOptions.businessCategories, viewingCompany._full?.accountType)}</p></div>
                  <div className="mb-3"><small className="text-muted d-block" style={{ fontSize: '11px', fontWeight: '600' }}>BUSINESS SECTOR</small><p className="mb-0" style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>{getDropdownName(dropdownOptions.businessSectors, viewingCompany._full?.businessSector)}</p></div>
                  <div className="mb-3"><small className="text-muted d-block" style={{ fontSize: '11px', fontWeight: '600' }}>SUBSCRIPTION PERIOD</small><p className="mb-0" style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>{viewingCompany.startDate} — {viewingCompany.endDate}</p></div>
                  <div><small className="text-muted d-block" style={{ fontSize: '11px', fontWeight: '600' }}>DATE ADDED</small><p className="mb-0" style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>{viewingCompany.createdDate}</p></div>
                </div>
                <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '20px', border: '1px solid #e5e7eb' }}>
                  <h6 style={{ color: '#F89423', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><i className="fas fa-user"></i> Contact Information</h6>
                  <div className="mb-3"><small className="text-muted d-block" style={{ fontSize: '11px', fontWeight: '600' }}>CONTACT PERSON</small><p className="mb-0" style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>{getDropdownName(dropdownOptions.titles, viewingCompany._full?.title)} {viewingCompany._full?.firstName} {viewingCompany._full?.lastName}</p></div>
                  <div className="mb-3"><small className="text-muted d-block" style={{ fontSize: '11px', fontWeight: '600' }}>EMAIL ADDRESS</small><p className="mb-0"><a href={`mailto:${viewingCompany.email}`} style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500', fontSize: '14px' }}>{viewingCompany.email}</a></p></div>
                  <div><small className="text-muted d-block" style={{ fontSize: '11px', fontWeight: '600' }}>PHONE NUMBER</small><p className="mb-0"><a href={`tel:${viewingCompany.phoneNumber}`} style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500', fontSize: '14px' }}>{viewingCompany.phoneNumber}</a></p></div>
                </div>
              </div>
              <div>
                <h6 style={{ color: '#F89423', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><i className="fas fa-file-alt"></i> Documents</h6>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '20px', textAlign: 'center', background: viewingCompany.ninDocument ? '#f0fdf4' : '#fef2f2' }}>
                    <i className="fas fa-id-card fa-2x mb-2" style={{ color: viewingCompany.ninDocument ? '#10b981' : '#ef4444' }}></i>
                    <h6 style={{ fontWeight: '600', color: '#374151', fontSize: '14px', marginBottom: '8px' }}>NIN Document</h6>
                    {viewingCompany.ninDocument ? (
                      <>
                        <span className="badge bg-success mb-2" style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px' }}><i className="fas fa-check-circle me-1"></i> Uploaded</span>
                        <div className="d-flex gap-2 justify-content-center mt-2">
                          <button className="btn btn-sm" onClick={() => viewDocument(viewingCompany.ninDocument, 'nin', 'NIN Document')} style={{ backgroundColor: '#F89423', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}><i className="fas fa-eye me-1"></i> View</button>
                          <button className="btn btn-sm" onClick={() => downloadDocument(viewingCompany.ninDocument, 'nin', 'NIN Document')} style={{ background: 'white', color: '#6b7280', border: '1px solid #d1d5db', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}><i className="fas fa-download me-1"></i> Download</button>
                        </div>
                      </>
                    ) : (
                      <span className="badge bg-danger" style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px' }}><i className="fas fa-times-circle me-1"></i> Not Uploaded</span>
                    )}
                  </div>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '20px', textAlign: 'center', background: viewingCompany.cacDocument ? '#f0fdf4' : '#fef2f2' }}>
                    <i className="fas fa-file-contract fa-2x mb-2" style={{ color: viewingCompany.cacDocument ? '#10b981' : '#ef4444' }}></i>
                    <h6 style={{ fontWeight: '600', color: '#374151', fontSize: '14px', marginBottom: '8px' }}>CAC Document</h6>
                    {viewingCompany.cacDocument ? (
                      <>
                        <span className="badge bg-success mb-2" style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px' }}><i className="fas fa-check-circle me-1"></i> Uploaded</span>
                        <div className="d-flex gap-2 justify-content-center mt-2">
                          <button className="btn btn-sm" onClick={() => viewDocument(viewingCompany.cacDocument, 'cac', 'CAC Document')} style={{ backgroundColor: '#F89423', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}><i className="fas fa-eye me-1"></i> View</button>
                          <button className="btn btn-sm" onClick={() => downloadDocument(viewingCompany.cacDocument, 'cac', 'CAC Document')} style={{ background: 'white', color: '#6b7280', border: '1px solid #d1d5db', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}><i className="fas fa-download me-1"></i> Download</button>
                        </div>
                      </>
                    ) : (
                      <span className="badge bg-danger" style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px' }}><i className="fas fa-times-circle me-1"></i> Not Uploaded</span>
                    )}
                  </div>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '20px', textAlign: 'center', background: viewingCompany.logo ? '#f0fdf4' : '#fef2f2' }}>
                    <i className="fas fa-image fa-2x mb-2" style={{ color: viewingCompany.logo ? '#10b981' : '#ef4444' }}></i>
                    <h6 style={{ fontWeight: '600', color: '#374151', fontSize: '14px', marginBottom: '8px' }}>Company Logo</h6>
                    {viewingCompany.logo ? (
                      <>
                        <span className="badge bg-success mb-2" style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px' }}><i className="fas fa-check-circle me-1"></i> Uploaded</span>
                        <div className="d-flex gap-2 justify-content-center mt-2">
                          <button className="btn btn-sm" onClick={() => viewDocument(viewingCompany.logo, 'logo', 'Company Logo')} style={{ backgroundColor: '#F89423', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}><i className="fas fa-eye me-1"></i> View</button>
                          <button className="btn btn-sm" onClick={() => downloadDocument(viewingCompany.logo, 'logo', 'Company Logo')} style={{ background: 'white', color: '#6b7280', border: '1px solid #d1d5db', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}><i className="fas fa-download me-1"></i> Download</button>
                        </div>
                      </>
                    ) : (
                      <span className="badge bg-danger" style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px' }}><i className="fas fa-times-circle me-1"></i> Not Uploaded</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <button type="button" onClick={() => { handleCloseViewModal(); handleOpenModal(viewingCompany); }} style={{ padding: '10px 20px', background: '#3b82f6', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontWeight: '500' }}><i className="fas fa-edit me-1"></i> Edit Company</button>
                <button type="button" onClick={handleCloseViewModal} style={{ padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD/EDIT COMPANY MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', width: '90%', maxWidth: '650px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, color: '#1f2937' }}>{editingCompany ? 'Edit Company' : 'Add New Company'}</h5>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Business Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="text" name="businessName" value={formData.businessName} onChange={handleChange} required placeholder="e.g., FSL Management Services" className={formErrors.businessName ? 'p-invalid' : ''} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                {formErrors.businessName && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.businessName}</small>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>First Name</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First name" style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Last Name</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last name" style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="email@company.com" className={formErrors.email ? 'p-invalid' : ''} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                  {formErrors.email && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.email}</small>}
                </div>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Phone Number</label>
                  <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="+234 801 234 5678" style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Title</label>
                  <select name="title" value={formData.title} onChange={handleChange} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: '#f8f9fa' }}>
                    <option value="">Select Title</option>
                    {dropdownOptions.titles.map(title => (<option key={title.id} value={title.id}>{title.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Account Type *</label>
                  <select name="accountType" value={formData.accountType} onChange={handleChange} required style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: '#f8f9fa' }}>
                    <option value="">Select Account Type</option>
                    {dropdownOptions.businessCategories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" name="isSchedule" id="isSchedule" checked={formData.isSchedule} onChange={handleChange} style={{ width: '18px', height: '18px' }} />
                  <label htmlFor="isSchedule" style={{ fontWeight: '500', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>Is Scheduled</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" name="isCompleteRegistration" id="isCompleteRegistration" checked={formData.isCompleteRegistration} onChange={handleChange} style={{ width: '18px', height: '18px' }} />
                  <label htmlFor="isCompleteRegistration" style={{ fontWeight: '500', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>Registration Complete</label>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Start Date</label>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>End Date</label>
                  <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className={formErrors.endDate ? 'p-invalid' : ''} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                  {formErrors.endDate && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.endDate}</small>}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Business Sector</label>
                <select name="businessSector" value={formData.businessSector} onChange={handleChange} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: '#f8f9fa' }}>
                  <option value="">Select Business Sector</option>
                  {dropdownOptions.businessSectors.map(sector => (<option key={sector.id} value={sector.id}>{sector.name}</option>))}
                </select>
              </div>
              <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" name="isOnboarded" id="isOnboarded" checked={formData.isOnboarded} onChange={handleChange} style={{ width: '18px', height: '18px' }} />
                <label htmlFor="isOnboarded" style={{ fontWeight: '500', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>Company is onboarded (active)</label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" onClick={handleCloseModal} disabled={submitting} style={{ padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', color: '#374151', cursor: 'pointer', fontWeight: '500', fontSize: '14px' }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{ padding: '10px 20px', background: submitting ? '#cbd5e1' : BRAND_ORANGE, border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {submitting ? <><i className="fas fa-spinner fa-spin"></i>Saving...</> : <><i className="fas fa-check"></i>{editingCompany ? 'Update' : 'Add'} Company</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSS Styles */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        /* ✅ Custom Scrollbar for Table Container */
        .table-scroll-wrapper::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        .table-scroll-wrapper::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .table-scroll-wrapper::-webkit-scrollbar-thumb {
          background: #F89423;
          border-radius: 10px;
          border: 2px solid #f1f1f1;
        }
        .table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
          background: #ea7c1f;
        }
        .table-scroll-wrapper {
          scrollbar-width: thin;
          scrollbar-color: #F89423 #f1f1f1;
        }

        /* Ensure DataTable doesn't overflow its container weirdly */
        .table-scroll-wrapper .p-datatable {
          min-width: 100%;
        }
        .table-scroll-wrapper .p-datatable-wrapper {
          overflow-x: visible !important;
        }
        
        * { scrollbar-width: thin; scrollbar-color: #F89423 #f1f1f1; }
        
        .admin-dashboard-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        
        /* Sidebar - Orange Theme */
        .sidebar {
          width: 260px; background: white; color: #333; position: fixed; left: 0; top: 0;
          height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000;
          border-right: 1px solid #e5e7eb;
        }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; }
        .brand-logo { color: #333; font-size: 24px; font-weight: 700; text-decoration: none; display: flex; align-items: center; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title {
          font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;
          color: #F89423; padding: 0 20px; margin-bottom: 10px;
        }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link {
          display: flex; align-items: center; padding: 12px 20px; color: #333; text-decoration: none;
          transition: all 0.3s ease; font-weight: 500;
        }
        .nav-link:hover, .nav-link.active { 
          background-color: #F89423; 
          color: white; 
          border-left: 4px solid white; 
        }
        .sidebar-footer { margin-top: auto; padding-bottom: 20px; }
        
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .top-header { 
          background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100;
        }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .header-icon { 
          width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6;
          color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; position: relative; 
        }
        .header-icon:hover { background: #e5e7eb; color: #F89423; }
        .header-icon .badge { 
          position: absolute; top: 5px; right: 5px; background: #F89423; color: white;
          font-size: 10px; padding: 2px 5px; border-radius: 10px; 
        }
        
        .card { border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); background: white; border: none; }
        .p-card { border-radius: 12px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important; }
        .p-button { border-radius: 8px !important; font-weight: 500; }
        .p-inputtext { border-radius: 8px !important; border: 1px solid #d1d5db !important; padding: 10px 14px !important; }
        .p-inputtext:focus { border-color: #F89423 !important; box-shadow: 0 0 0 2px rgba(248,148,35,0.2) !important; }
        .p-dropdown { border-radius: 8px !important; }
        .p-invalid { border-color: #ef4444 !important; }
        .p-datatable .p-datatable-thead > tr > th { background: #f9fafb !important; font-weight: 600 !important; font-size: 13px !important; color: #374151 !important; }
        .p-datatable .p-datatable-tbody > tr > td { padding: 12px 16px !important; font-size: 14px !important; }
        .p-datatable .p-paginator { background: #f8f9fa !important; }
        .p-dropdown-panel { z-index: 2100 !important; }
        
        /* Toggle Switch Styles */
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 26px;
        }
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #d1fae5;
          transition: 0.3s;
          border-radius: 26px;
          border: 2px solid #10b981;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 2px;
          bottom: 2px;
          background-color: #10b981;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle-switch input:checked + .toggle-slider {
          background-color: #f3f4f6;
          border-color: #6b7280;
        }
        .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(24px);
          background-color: #6b7280;
        }
        .toggle-switch input:disabled + .toggle-slider {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .modal-overlay { animation: fadeIn 0.3s ease; }
        .modal-panel { animation: slideIn 0.3s ease; }
        
        @media (max-width: 992px) { 
          .sidebar { transform: translateX(-100%); } 
          .sidebar.sidebar-open { transform: translateX(0); } 
          .main-content { margin-left: 0; } 
        }
        @media (max-width: 768px) { 
          .content-wrapper { padding: 15px; } 
          .top-header { padding: 15px; } 
          .modal-panel { width: 95% !important; } 
        }
      `}</style>
    </div>
  );
}

export default Companies;